import {
  Card, Group, Text, Title, Badge, Stack, Center, Avatar, Box, ActionIcon,
  Button, ThemeIcon,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowLeft, IconPencil, IconBook2, IconCash, IconShieldCheck, IconAddressBook } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { formatPhone } from '../lib/format';
import { StaffPhotoControl } from '../components/StaffPhotoControl';
import { StaffDeleteButton } from '../components/StaffDeleteButton';
import { Specialty } from '../components/Specialty';
import { ContactsManager } from '../components/ContactsManager';
import { BlockControl } from '../components/BlockControl';
import { StaffEditModal, type StaffEditableUser } from '../components/StaffEditModal';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';
import { StaffPayrollSection } from '../components/payroll/StaffPayrollTabs';
import { useAuth } from '../contexts/AuthContext';
import { canAccess, isTenantStaff } from '../lib/permissions';

interface TeacherFull extends StaffEditableUser {
  isActive: boolean;
  blockReason?: string | null;
  createdAt: string;
  /** S3 kaliti yoki `sticker:<...>` — GET /staff/:id hali qaytarmaydi, qiymat
      foto yuklangach POST javobidan cache'ga yoziladi (StaffPhotoControl izohi) */
  avatar?: string | null;
}
interface Assignment { id: string; subject: { id: string; name: string }; class: { id: string; name: string } }

/** O'qituvchi kartochkasi (single page): profil, bloklash+sabab, biriktirishlar, kontaktlar,
 *  davomat (kelib-ketish), maosh va olgan maosh tarixi. */
export default function TeacherDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editOpen, editH] = useDisclosure(false);
  const { user } = useAuth();
  // Maosh/davomat `/payroll/*` — FINANCE; ruxsati yo'q rolga bo'lim umuman chizilmaydi
  const canPayroll = canAccess(user?.role, 'payments');

  const { data: teacher, isLoading } = useQuery({
    queryKey: ['teacher', id],
    queryFn: async () => (await api.get<TeacherFull>(`/staff/${id}`)).data,
    enabled: !!id,
  });
  const { data: assignments = [] } = useQuery({
    queryKey: ['teacher-assignments', id],
    queryFn: async () => (await api.get<Assignment[]>('/assignments', { params: { teacherId: id } })).data,
    enabled: !!id,
  });

  // O'qituvchi Staff ro'yxatida (staff-all) va dropdownlarда (teachers) ham ko'rinadi — hammasini yangilaymiz.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['teacher', id] });
    qc.invalidateQueries({ queryKey: ['teachers-page'] });
    qc.invalidateQueries({ queryKey: ['staff-all'] });
    qc.invalidateQueries({ queryKey: ['staff', id] });
    qc.invalidateQueries({ queryKey: ['teachers'] });
  };

  if (isLoading) return <BlockLoader variant="card" />;
  if (!teacher) return <Center h={300}><Text c="dimmed">{t('teacherDetail.notFound')}</Text></Center>;

  const genderLabel = teacher.gender === 'MALE' ? t('teacherDetail.male') : teacher.gender === 'FEMALE' ? t('teacherDetail.female') : t(`roles.${teacher.role}`);

  return (
    <Stack>
      {/* Sarlavha */}
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/teachers')}><IconArrowLeft size={18} /></ActionIcon>
        <Card style={{ flex: 1 }} p="md">
          <Group justify="space-between">
            <Group>
              {/* fileUrl stiker (`sticker:<...>`) uchun undefined qaytaradi — bosh harf ko'rinadi */}
              <Avatar color="blue" radius="xl" size={54} src={fileUrl(teacher.avatar)} alt={teacher.fullName}>{teacher.fullName[0]?.toUpperCase()}</Avatar>
              <Box>
                <Group gap="xs">
                  <Title order={4}>{teacher.fullName}</Title>
                  <Badge variant="light" color={teacher.role === 'HOMEROOM_TEACHER' ? 'grape' : 'blue'}>{genderLabel}</Badge>
                  <Specialty value={teacher.specialty} badge />
                  {!teacher.isActive && <Badge color="gray">{t('block.blocked')}</Badge>}
                  {teacher.canEnterTestScores && <Badge color="grape" variant="light">{t('examsPage.testTeacher')}</Badge>}
                </Group>
                <Group gap="xs" mt={2}>
                  <Text size="sm" c="dimmed" ff="monospace">{teacher.login}</Text>
                  <Text size="sm" c="dimmed">{formatPhone(teacher.phone)}</Text>
                </Group>
                {/* Yuz tanish terminali uchun foto. invalidate() EMAS — GET javobida
                    `avatar` yo'q, refetch endigina yozilgan qiymatni o'chirib yuborardi. */}
                <StaffPhotoControl
                  userId={id}
                  avatar={teacher.avatar}
                  onChanged={(a) => qc.setQueryData<TeacherFull>(['teacher', id], (old) => (old ? { ...old, avatar: a } : old))}
                />
              </Box>
            </Group>
            <Group gap="xs">
              <Button variant="light" leftSection={<IconCash size={16} />} onClick={() => navigate(`/staff/${id}`)}>{t('teacherDetail.salaryLink')}</Button>
              <Button leftSection={<IconPencil size={16} />} onClick={editH.open}>{t('teacherDetail.edit')}</Button>
              <StaffDeleteButton id={id!} fullName={teacher.fullName} backTo="/teachers" />
            </Group>
          </Group>
        </Card>
      </Group>

      {/* Holat / bloklash — sabab bilan tasdiqlash `BlockControl` ichida (modal ichida modal bo'lmasin) */}
      <SectionCard title={t('teacherDetail.status')} icon={IconShieldCheck}>
        <BlockControl patchPath={`/staff/${id}`} isActive={teacher.isActive} blockReason={teacher.blockReason} onDone={invalidate} />
      </SectionCard>

      {/* Biriktirishlar (fan · sinf) */}
      <SectionCard title={t('teacherDetail.assignments')} icon={IconBook2} right={<Badge variant="light" color="gray">{assignments.length}</Badge>}>
        {!assignments.length ? (
          <Text c="dimmed" size="sm">{t('teacherDetail.noAssignments')}</Text>
        ) : (
          <Stack gap="xs">
            {assignments.map((a) => (
              <Group key={a.id} justify="space-between" p="xs" style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8 }}>
                <Group gap="sm">
                  <ThemeIcon variant="light" color="blue" size={34} radius="md"><IconBook2 size={18} /></ThemeIcon>
                  <Text fw={600} size="sm">{a.subject.name}</Text>
                </Group>
                <Badge variant="light" color="grape">{a.class.name}</Badge>
              </Group>
            ))}
          </Stack>
        )}
      </SectionCard>

      {/*
        Kontaktlar — FAQAT KO'RISH. Tahrirlash modali telefon/manzilni "o'rniga qo'yish"
        (replace) usulida yuboradi; bittalab tahrirlaydigan `ContactsManager` bilan
        aralashtirilsa qatorlar id lari kutilmaganda almashadi (backend kontrakti).
      */}
      <SectionCard
        title={t('teacherDetail.contacts')}
        icon={IconAddressBook}
        right={(
          <Button size="compact-sm" variant="subtle" leftSection={<IconPencil size={14} />} onClick={editH.open}>
            {t('teacherDetail.edit')}
          </Button>
        )}
      >
        <ContactsManager owner={{ userId: teacher.id }} phones={teacher.phones ?? []} addresses={teacher.addresses ?? []} queryKey={['teacher', id]} readOnly />
      </SectionCard>

      {/* Davomat (kelib-ketish), maosh hisobi va olgan maosh tarixi — xodim sahifasi
          bilan YAGONA komponent: o'qituvchi ham xodim, ma'lumoti `/payroll/*` da */}
      {(canPayroll || isTenantStaff(user)) && <StaffPayrollSection userId={teacher.id} money={canPayroll} />}

      {/* To'liq tahrirlash — xodim kartochkasi bilan YAGONA komponent (T2) */}
      <StaffEditModal
        opened={editOpen}
        onClose={editH.close}
        staff={teacher}
        onSaved={invalidate}
        title={t('teacherDetail.edit')}
      />
    </Stack>
  );
}
